import moment from 'moment';
import { useEffect, useState } from 'react';
import { useMutation } from 'react-query';
import { useLocation } from 'react-router';
import { useTable } from 'react-table';
import { useAppContext } from '../../appProvider/contextProvider';
import HelperComponent from '../../components/HelperComponent/HelperComponent';
import { getSubscriptionById } from '../../services/shopifySubscription';
import { getInvoices } from '../../services/stripe';
import getAmount from '../../Util/getAmount';


const columns =  [
    {
        Header: 'Subscription',
        accessor: 'subscription', // accessor is the "key" in the data
    },
    {
        Header: 'Invoice Date',
        accessor: 'period_start', // accessor is the "key" in the data
        Cell: (props) => props.value ? moment(props.value).format('MMM DD, YYYY') : ''
    }, 
    {
        Header: 'Subscription Duration',
        Cell: ({row}) => `${moment(row.original.period_start).format('MMM DD, YYYY')} - ${moment(row.original.period_end).format('MMM DD, YYYY')}`,
    },
    {
        Header: 'Amount',
        accessor: 'amount_paid', // accessor is the "key" in the data
        Cell: (props) => `$${props.value || 0}`
    },
    {
        Header: 'Status',
        accessor: 'status',
        Cell: (props) => <span className="titleCase">{props.value}</span>
    },
    {
        Header: '',
        accessor: 'hosted_invoice_url', // accessor is the "key" in the data
        Cell: (props) => <a href={props.value} target="_blank" className="terms-link" >Invoice</a>
    },
];

const BillingsAndPlanning = () => {

    const { 
        selectedWebsite,
        planDetails,
        setPricingPopupOpen, 
        setCancelPlanPopupOpen,
        setChosenPlan,
        setIsVerifyPopupOpen,
        refetchProfile,
        isShopify,
        setPaymentSuccess,
        setPaymentFailure,
        isPromotionalUser,
        isShopifyFreeUser
    } = useAppContext();

    
    const location = useLocation();

    const [invoices, setInvoices] = useState([]);


    const getShopifyInvoiceWithId = useMutation((charge_id) => {
        return getSubscriptionById(charge_id);
    }, {
        onSuccess: (data) => {
            if(data.error || data.errors) {
                setPaymentFailure(true);   
            }
            else if(data.status === 'active') {
                setPaymentSuccess(true);
            }
        }
    })

    const getNormalInvoice = useMutation(() => {
        return getInvoices();
    }, {
        onSuccess: (data) => {
            data && setInvoices([...data]);
        }
    })

    useEffect(() => {
        if(selectedWebsite) {
            if(isShopify && location.search.includes('charge_id')) {
                const params = new URLSearchParams(location.search);
                const charge_id = params.get('charge_id');
                getShopifyInvoiceWithId.mutate(charge_id);
            }
            else {
                getNormalInvoice.mutate();
            }
        }
    }, [isShopify, selectedWebsite]);
    

    useEffect(() => {
        refetchProfile();
    },[]);

    
    const isSubscriptionBased = planDetails?.subscription_type === 'subscriber-based';

    const getFormattedDate = (date) => {
        return moment(date).format('MMM DD, YYYY')
    }

    const getPlanName = () => {
        if(planDetails?.is_custom) return 'enterprise';
        else if(planDetails?.is_default) return 'free';
        else return 'premium';
    }

    const onRenew = () => {
        setChosenPlan(planDetails);
        setIsVerifyPopupOpen(true);
    }

    const invoicesTableInstance = useTable({
        columns,
        data: isShopify ? [] : invoices,
    });

    const showCancel = () => {
        if(isShopify && !isShopifyFreeUser) {
            return planDetails.status !== 'cancelled';
        }
        return !planDetails?.is_default && !planDetails?.cancel_at_period_end;
    }

    const showRenewalText = () => {
        if(isShopify && !isShopifyFreeUser) {
            return planDetails.status === 'cancelled' ? 'ends' : 'renews';
        }
        if(isPromotionalUser) {
            return 'ends';
        }
        return planDetails?.cancel_at_period_end ? 'ends' : 'renews';
    }

    const {
        getTableProps,
        getTableBodyProps,
        headerGroups,
        rows,
        prepareRow,
      } = invoicesTableInstance;

    return <div className="panel full-height">
        <div className="panel-header">
            <div className="d-flex justify-content-between align-items-center">
            </div>
            <div className="d-flex justify-content-between align-items-space-between">
                    <div>
                    <h3 className="mb-0">
                        Billing / Pricing
                        <HelperComponent
                            component={'Billing / Pricing'}
                            href="https://chirpyweb.com/documentation_posts/manage-chirpyweb-billing-and-plans/" />
                    </h3>
                    </div>
                   { planDetails && <div className="d-flex justify-content-between " style={{gap: '10px'}}>
                       { showCancel() && <button onClick={() => setCancelPlanPopupOpen(true) } className="btn btn-danger">Cancel Current Plan</button> }
                       { planDetails?.cancel_at_period_end && <button onClick={onRenew} className="btn btn-outline">Renew Plan</button> }
                    </div> }
                </div>                                 
        </div>
        <div className="panel-body full-height">
            <h5 className="medium">Plan Information</h5>
            <div className="box bg-gray">
                <div className="row justify-content-between align-items-center">
                    <div className="col-12 col-sm-5 col-xl-5">
                        <h5 className="regular titleCase">{getPlanName()}</h5>
                        <h6 className="regular">
                            {!isPromotionalUser && <>${getAmount(planDetails?.amount)}/{planDetails?.interval} -</>} {planDetails?.subscription_thresholds} 
                            {isSubscriptionBased ? ' subscribers' : ' impressions per calendar month'}</h6>
                        <p className="thin">
                            {planDetails?.is_default ? <i>Lifetime free</i> :
                                <i>Your plan { showRenewalText() } on {getFormattedDate(planDetails?.current_period_end)}.</i> 
                            }
                            </p>
                    </div>
                    <div className="col-12 col-sm-7 col-xl-7">
                    <div className="row align-items-center justify-content-end">
                        <div className="col-12 col-sm-3 col-xl-3 my-2 threshold-wrapper">
                            <label>Websites</label>
                            <div className="progress">
                                <div 
                                    className="progress-bar bg-primary" role="progressbar"     
                                    style={{
                                        width: `${(planDetails?.website_consumed/planDetails?.website_thresholds) * 100}%`, 
                                        background: planDetails?.website_consumed > planDetails?.website_thresholds && 'var(--warning-color)'
                                    }}  aria-valuenow="25" aria-valuemin="0" aria-valuemax="100">
                                </div>
                          </div>  
                          <p className="light"><small>{planDetails?.website_consumed}/{planDetails?.website_thresholds}</small></p>
                        </div>
                        <div className="col-12 col-sm-4 col-xl-6 my-2 threshold-wrapper">
                                <label>
                                    { 
                                        isSubscriptionBased
                                        ? 'Subscribers'
                                        : 'Impressions'
                                    }
                                </label>
                                <div className="progress">
                                    <div className="progress-bar bg-primary" 
                                        role="progressbar" 
                                        style={{width: `${(planDetails?.subscription_consumed/planDetails?.subscription_thresholds) * 100}%`,
                                                background: planDetails?.subscription_consumed > planDetails?.subscription_thresholds && 'var(--warning-color)'
                                            }} 
                                        aria-valuenow="25" aria-valuemin="0" aria-valuemax="100"
                                    ></div>                                                
                                </div>    
                            <p className="light"><small>{planDetails?.subscription_consumed}/{planDetails?.subscription_thresholds}</small></p>
                            <p className="threshold-value ">{!isSubscriptionBased && <small className="text-light thin"><i>Your impressions limit resets on <span>{getFormattedDate(planDetails?.current_period_end)}</span></i></small>}</p>
                        </div>
                        <div className="col-12 col-sm-3 col-xl-3 my-2">
                            <button className="btn btn-primary" onClick={() => setPricingPopupOpen(true)}>
                                Change Plan
                            </button> 
                        </div>
                    </div>
                    </div>
                </div>
            </div>
            {
                (selectedWebsite && !isShopify) && <>
                    <h5 className="medium mt-5">Invoices</h5>
                    <div className="table-responsive mt-1 half-height scrollable_y">
                            <table className="table table-hover subscribers-table " {...getTableProps()} >
                                <thead>
                                    {
                                        headerGroups.map(headerGroup => (
                                            <tr {...headerGroup.getHeaderGroupProps()}>
                                                {
                                                    headerGroup.headers.map(column => (
                                                        <th {...column.getHeaderProps()}>
                                                            {column.render('Header')}
                                                        </th>
                                                    ))
                                                }
                                            </tr>
                                        ))
                                    }
                                </thead>
                                <tbody {...getTableBodyProps()}>
                                    {   // Loop over the table rows
                                        rows.map(row => {
                                            // Prepare the row for display
                                            prepareRow(row)
                                            return (
                                            // Apply the row props
                                            <tr {...row.getRowProps()}>
                                                {// Loop over the rows cells
                                                row.cells.map(cell =>  <td {...cell.getCellProps()}>
                                                {// Render the cell contents
                                                    cell.render('Cell')}
                                                </td>)}
                                            </tr>
                                            )
                                        })
                                    }
                                </tbody>
                            </table>                                           
                        </div> 
                </>
            }
        </div>
    </div>
}


export default BillingsAndPlanning;